import InputText from "../InputText";
import isEmpty from "is-empty";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { convertSuggestionToStreetname } from "@utils/tools";
import { LocationOnOutlined } from "@mui/icons-material";
import { useSession } from "@hooks";

type InputPlacesAutocompleteProps = {
  defaultValue: string;
  setSelectedSuggestion: (value: any | null) => void;
};

//API KEY MAPBOX
const MAPBOX_API_KEY = process.env.REACT_APP_MAPBOX_GEOCODING_API_KEY ?? "";

//TYPING TIMEOUT
const TYPING_TIMEOUT = process.env.REACT_APP_MAPBOX_GEOCODING_TYPING_TIMEOUT
  ? parseInt(process.env.REACT_APP_MAPBOX_GEOCODING_TYPING_TIMEOUT)
  : 700;

//FETCH LIMIT PER USER (local state)
const FETCH_LIMIT = process.env.REACT_APP_MAPBOX_GEOCODING_FETCH_LIMIT
  ? parseInt(process.env.REACT_APP_MAPBOX_GEOCODING_FETCH_LIMIT)
  : 5;

//FETCH LENGTH - FETCH API AFTER X LETTERS
const FETCH_LENGTH = process.env.REACT_APP_MAPBOX_GEOCODING_FETCH_LENGTH
  ? parseInt(process.env.REACT_APP_MAPBOX_GEOCODING_FETCH_LENGTH)
  : 3;

const InputPlacesAutocomplete: React.FC<InputPlacesAutocompleteProps> = ({
  defaultValue,
  setSelectedSuggestion,
}) => {
  /* Hooks */
  const { getLanguageVarValue } = useSession();

  /* States */
  const [typingTimeout, setTypingTimeout] =
    useState<NodeJS.Timeout | null>(null);
  const [value, setValue] = useState(defaultValue);
  const [suggestions, setSuggestions] = useState<any>([]);
  const [focused, setFocused] = useState<boolean>(false);

  const [count, setCount] = useState<number>(0);

  /* Effects */
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);

  /* Functions */
  const fetchAutocompleteResults = async (endpoint: string) => {
    const response = await fetch(endpoint);
    const results = await response.json();
    setSuggestions(results?.features);
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const _value = event.target.value;
    setValue(_value);
    if (count <= FETCH_LIMIT && _value.length >= FETCH_LENGTH) {
      try {
        const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${event.target.value}.json?access_token=${MAPBOX_API_KEY}&autocomplete=true`;
        // Clear the previous timeout
        if (typingTimeout) {
          clearTimeout(typingTimeout);
        }

        // Set a new timeout to fetch results after 1 second of inactivity
        const newTimeout = setTimeout(() => {
          setCount(count + 1);
          fetchAutocompleteResults(endpoint);
        }, TYPING_TIMEOUT);

        setTypingTimeout(newTimeout);
      } catch (error) {}
    } else {
      setSuggestions(null);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setFocused(false);
    }, 300);
  };

  const handleSelectSuggestion = (suggestion: any) => {
    setSelectedSuggestion(suggestion);
    setValue(convertSuggestionToStreetname(suggestion));
    setSuggestions([]);
  };

  return (
    <div className="tw-relative">
      <InputText
        additionalClassname="tw-w-full"
        placeholder={getLanguageVarValue("LANG_neobooking_adr_street") + "*"}
        onChange={handleChange}
        value={value}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
      />

      {!isEmpty(suggestions) && focused && (
        <div className="tw-text-sm tw-flex tw-flex-col tw-gap-y-2 tw-border tw-mt-1 tw-bg-white tw-shadow tw-rounded tw-absolute tw-w-full tw-z-50">
          {suggestions.map((suggestion: any, index: number) => {
            return (
              <div className="tw-flex hover:tw-bg-gray-100 tw-cursor-pointer tw-w-full tw-py-1">
                <LocationOnOutlined />
                <div
                  key={index}
                  onClick={() => handleSelectSuggestion(suggestion)}
                >
                  {suggestion.place_name}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

InputPlacesAutocomplete.propTypes = {
  //
};

export default InputPlacesAutocomplete;
